import {
  Button,
  DatePicker,
  Form,
  FormProps,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Switch,
} from "antd";
import { Calendar, Globe, MapPin, Truck, X } from "lucide-react";
import { FC, useEffect } from "react";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import {
  useCreateShuttle,
  useGetShuttleById,
  useGetVesselsForDropdown,
  useUpdateShuttle,
  VESSEL_KEYS,
} from "~/services/vessel";
import { useShuttleStore } from "../store/shuttle.store";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "~/hooks";

type Props = {};

export const ShuttleModal: FC<Props> = () => {
  const [form] = Form.useForm();
  const qc = useQueryClient();

  const { success, error } = useNotification();

  const { formState, filters, closeShuttleModal } = useShuttleStore();

  const { data: shuttle, isLoading: isLoadingShuttle } = useGetShuttleById(
    formState.shuttleId
  );

  const { data: vessels } = useGetVesselsForDropdown();

  const postToNetwork = Form.useWatch("postToNetwork", form);

  useEffect(() => {
    if (!shuttle) return;
    form.setFieldsValue({
      origin: shuttle.origin,
      destination: shuttle.destination,
      loadDate: shuttle.loadDate ? dayjs(shuttle.loadDate) : null,
      departureDate: shuttle.departureDate
        ? dayjs(shuttle.departureDate)
        : null,
      arrivalDate: shuttle.arrivalDate ? dayjs(shuttle.arrivalDate) : null,
      returnDate: shuttle.returnDate ? dayjs(shuttle.returnDate) : null,
      vesselIds: shuttle.vessels.map((vessel) => vessel.id),
      postToNetwork: !!shuttle.networkRouteId,
    });
  }, [shuttle, form]);

  const createMutation = useCreateShuttle();
  const updateMutation = useUpdateShuttle();

  const handleSubmit: FormProps["onFinish"] = (values) => {
    if (
      !values.loadDate ||
      !values.departureDate ||
      !values.arrivalDate ||
      !values.returnDate
    ) {
      error({
        message: "Missing required fields",
        description: "Please fill in all required date fields.",
      });
      return;
    }

    const formattedValues = {
      ...values,
      loadDate: values.loadDate.toISOString(),
      departureDate: values.departureDate.toISOString(),
      arrivalDate: values.arrivalDate.toISOString(),
      returnDate: values.returnDate.toISOString(),
    };

    const mutation = formState.shuttleId ? updateMutation : createMutation;
    const payload = formState.shuttleId
      ? { id: formState.shuttleId, ...formattedValues }
      : formattedValues;

    mutation.mutate(payload, {
      onSuccess: () => {
        success({
          message: formState.shuttleId
            ? "Shuttle updated successfully"
            : "Shuttle created successfully",
        });
        closeShuttleModal();
        qc.invalidateQueries({
          queryKey: VESSEL_KEYS.GET_SHUTTLES(filters),
        });
      },
      onError: (err) => {
        console.error(
          `Error ${formState.shuttleId ? "updating" : "creating"} shuttle:`,
          err
        );
        error({
          message: `Error ${
            formState.shuttleId ? "updating" : "creating"
          } shuttle`,
          description: err.message,
        });
      },
    });
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const modalTitle = shuttle
    ? "Edit Shuttle " + (shuttle.shuttleRef || "")
    : "New Shuttle";

  return (
    <Modal
      width={"70%"}
      open={formState.open}
      onCancel={closeShuttleModal}
      footer={null}
      classNames={{
        body: "p-0",
        content: "p-0",
      }}
    >
      <Spin spinning={isLoadingShuttle}>
        <Form
          form={form}
          layout="vertical"
          size="large"
          requiredMark={CustomizeRequiredMark}
          className="space-y-6"
          onFinish={handleSubmit}
        >
          <div className="mx-auto bg-white rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {modalTitle}
              </h3>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  {/* Origin & Destination */}
                  <div>
                    <Form.Item
                      name="origin"
                      label="Origin"
                      rules={[{ required: true, message: "Please enter origin" }]}
                      className="mb-0"
                    >
                      <Input
                        prefix={<MapPin size={16} className="text-gray-400 mr-2" />}
                        placeholder="e.g. Brooklyn, NY"
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <Form.Item
                      name="destination"
                      label="Destination"
                      rules={[{ required: true, message: "Please enter destination" }]}
                      className="mb-0"
                    >
                      <Input
                        prefix={<MapPin size={16} className="text-gray-400 mr-2" />}
                        placeholder="e.g. Miami, FL"
                      />
                    </Form.Item>
                  </div>

                  {/* Dates */}
                  <div>
                    <Form.Item
                      name="loadDate"
                      label="Load Date"
                      rules={[{ required: true, message: "Please select a load date" }]}
                      className="mb-0"
                    >
                      <DatePicker
                        prefix={<Calendar size={16} className="text-gray-400 mr-2" />}
                        suffixIcon={null}
                        className="w-full"
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <Form.Item
                      name="departureDate"
                      label="Departure Date"
                      rules={[{ required: true, message: "Please select a departure date" }]}
                      className="mb-0"
                    >
                      <DatePicker
                        prefix={<Calendar size={16} className="text-gray-400 mr-2" />}
                        suffixIcon={null}
                        className="w-full"
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <Form.Item
                      name="arrivalDate"
                      label="Arrival Date"
                      rules={[{ required: true, message: "Please select an arrival date" }]}
                      className="mb-0"
                    >
                      <DatePicker
                        prefix={<Calendar size={16} className="text-gray-400 mr-2" />}
                        suffixIcon={null}
                        className="w-full"
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <Form.Item
                      name="returnDate"
                      label="Return Date"
                      rules={[{ required: true, message: "Please select a return date" }]}
                      className="mb-0"
                    >
                      <DatePicker
                        prefix={<Calendar size={16} className="text-gray-400 mr-2" />}
                        suffixIcon={null}
                        className="w-full"
                      />
                    </Form.Item>
                  </div>

                  {/* Vessels */}
                  <div className="col-span-2">
                    <Form.Item
                      name="vesselIds"
                      label="Vessels"
                      className="mb-0"
                    >
                      <Select
                        mode="multiple"
                        options={vessels?.map((vessel) => ({
                          label: (
                            <div className="flex items-center">
                              <Truck size={16} className="text-gray-400 mr-2" />
                              <div>
                                <div className="font-medium">{vessel.name}</div>
                                <div className="text-xs text-gray-500">
                                  {vessel.vesselType.name} • {vessel.licensePlate}
                                </div>
                              </div>
                            </div>
                          ),
                          value: vessel.id,
                        }))}
                        prefix={<Truck size={16} className="text-gray-400 mr-2" />}
                        className="w-full"
                        placeholder="Select Vessels"
                        maxTagCount={0}
                        maxTagPlaceholder={(omittedValues) =>
                          `${omittedValues.length} vessel(s) selected`
                        }
                      />
                    </Form.Item>

                    <Form.Item dependencies={["vesselIds"]} noStyle>
                      {({ getFieldValue, setFieldValue }) => {
                        const selectedVesselIds = getFieldValue("vesselIds") as string[];
                        if (!selectedVesselIds || selectedVesselIds.length === 0) return null;

                        return (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {selectedVesselIds.map((vesselId) => {
                              const vessel = vessels?.find((v) => v.id === vesselId);
                              return vessel ? (
                                <div
                                  key={vesselId}
                                  className="inline-flex items-center bg-blue-50 border border-blue-100 rounded-md px-2 py-1"
                                >
                                  <span className="text-xs font-medium text-blue-700">
                                    {vessel.name}
                                  </span>
                                  <button
                                    type="button"
                                    className="ml-1 text-blue-400 hover:text-blue-600"
                                    onClick={() =>
                                      setFieldValue(
                                        "vesselIds",
                                        selectedVesselIds.filter((id) => id !== vesselId)
                                      )
                                    }
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ) : null;
                            })}
                          </div>
                        );
                      }}
                    </Form.Item>
                  </div>

                  {/* Post to Network Toggle */}
                  {!formState.shuttleId && (
                    <div className="col-span-2 p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-sm font-medium flex items-center gap-2">
                          <Globe size={16} className="text-gray-700" />
                          Post to Network
                        </label>
                        <Form.Item name="postToNetwork" valuePropName="checked" className="mb-0">
                          <Switch size="small" />
                        </Form.Item>
                      </div>
                      <p className="text-xs text-gray-500 mb-0">
                        Share available capacity with other companies on the network
                      </p>

                      {postToNetwork && (
                        <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-4">
                          <Form.Item
                            name="maxVolume"
                            label="Max Volume (ft³)"
                            rules={[{ required: true, message: "Required" }]}
                            className="mb-0"
                          >
                            <InputNumber min={0} step={0.5} className="w-full" placeholder="e.g. 200" />
                          </Form.Item>
                          <Form.Item name="costPerCube" label="Cost per Cube ($)" className="mb-0">
                            <InputNumber min={0} step={0.5} className="w-full" placeholder="e.g. 15" />
                          </Form.Item>
                          <Form.Item name="minimumCost" label="Minimum Cost ($)" className="mb-0">
                            <InputNumber min={0} className="w-full" placeholder="e.g. 250" />
                          </Form.Item>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4 pt-4 border-t">
                  <Button
                    htmlType="button"
                    onClick={closeShuttleModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    htmlType="submit"
                    type="primary"
                    className="bg-black hover:bg-gray-800 border-black"
                    loading={isLoading}
                  >
                    {formState.shuttleId ? "Update Shuttle" : "Create Shuttle"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};
